import { T } from '@tolgee/react';
import { Modal } from 'antd';
import { FunctionComponent, MouseEvent, ReactNode, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import theme from '../../../assets/theme';
import { useWindowSize } from '../../../hooks/useWindowSize';

// Warm cream tone used for the "thanks" header — not in theme.colors, so kept
// as a local constant. Matches --curi-landing-beige in the design system file.
export const LANDING_BEIGE = '#FFF7EF';
export const ORANGE_TINT = theme.colors.lightOrangeBg; // #FDE7D1

// ---------------- Animations ----------------
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ---------------- ModalShell ----------------
export type ModalTone = 'beige' | 'cream' | 'orange' | 'lavender';

export const TONE_BG: Record<ModalTone, string> = {
  beige: theme.colors.beigeDark,
  cream: LANDING_BEIGE,
  orange: ORANGE_TINT,
  lavender: theme.colors.schoolPlanNudgeBg,
};

interface ModalShellProps {
  visible: boolean;
  onClose: () => void;
  width?: number;
  children: ReactNode;
  maskClosable?: boolean;
}

const Shell = styled.div`
  background: #fff;
  border-radius: 15px;
  overflow: hidden;
  font-family: 'Nunito', -apple-system, BlinkMacSystemFont, sans-serif;
  color: ${theme.colors.black};
  position: relative;
  animation: ${slideUp} 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  /* Sits above both the modal content and the decorative sticker in the
     header. Also explicitly re-enables pointer-events because the button is a
     sibling of .ant-modal-content (inside .ant-modal-wrap, which sets
     pointer-events: none for click-outside-to-close). */
  z-index: 10;
  pointer-events: auto;
  background: #fff;
  border: none;
  border-radius: 999px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${theme.colors.black};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  transition: background 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: ${theme.colors.beigeDark};
  }
`;

/** Marks a modal as one that must outrank ordinary modals — see globals.css. */
export const BLOCKING_MODAL_CLASS = 'curipod-blocking-modal';

export const ModalShell: FunctionComponent<ModalShellProps> = ({
  visible,
  onClose,
  width = 500,
  children,
  maskClosable = true,
}) => {
  const [windowWidth] = useWindowSize();
  const modalWidth = windowWidth < 600 ? '95%' : width;

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      closable={false}
      maskClosable={maskClosable}
      width={modalWidth}
      // Every modal built on this shell interrupts something — a paywall, a
      // trial limit, a gate — so it has to sit above whatever it interrupted.
      // antd gives all modals the same z-index (pinned to 3000 in globals.css),
      // which leaves the winner decided by portal insertion order: the School
      // Plan trial modal opened from inside the curriculum lesson preview
      // landed *behind* it, so pressing Present looked like it did nothing.
      // See the matching rule in styles/globals.css.
      wrapClassName={BLOCKING_MODAL_CLASS}
      style={{ top: 40 }}
      bodyStyle={{
        padding: 0,
        background: 'transparent',
        borderRadius: 15,
        overflow: 'hidden',
      }}
      modalRender={node => (
        <Shell>
          {node}
          <CloseButton aria-label="Close" onClick={onClose}>
            <XIcon />
          </CloseButton>
        </Shell>
      )}
    >
      {children}
    </Modal>
  );
};

// ---------------- Inline SVG icons ----------------
// Tiny rounded-stroke icons matching the prototype. Inlined rather than reusing
// project-wide SVG components because the stroke weight / size tuning here is
// specific to the modal's visual language.
export const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width={16}
    height={16}
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 6 12 12M6 18 18 6" />
  </svg>
);

export const LockIcon = (props: { style?: React.CSSProperties }) => (
  <svg
    viewBox="0 0 24 24"
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={props.style}
  >
    <rect x={5} y={11} width={14} height={9} rx={2} />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

// Filled Curipod crown for school-plan badges. Mirrors the glyph used by
// SchoolPlanAccentIcon (via EnterprisePlanIcon) so the modal badge reads as
// the same brand mark as the home-page card chip.
export const MiniCrownIcon = (props: { style?: React.CSSProperties }) => (
  <svg
    viewBox="0 0 428 315"
    width={14}
    height={14}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    style={props.style}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M232.163 64.9688C242.419 58.9743 249.309 47.8466 249.309 35.1096C249.309 16.0222 233.836 0.548828 214.748 0.548828C195.661 0.548828 180.188 16.0222 180.188 35.1096C180.188 47.8446 187.076 58.9708 197.329 64.9661L157.682 162.006C155.393 167.607 148.777 170.018 143.422 167.202L44.997 115.446C45.3067 113.951 45.4694 112.403 45.4694 110.816C45.4694 98.2801 35.3069 88.1177 22.7709 88.1177C10.2349 88.1177 0.0723877 98.2801 0.0723877 110.816C0.0723877 122.771 9.31454 132.567 21.0437 133.45L55.8774 239.604C56.0693 240.188 56.2057 240.777 56.2897 241.364H373.201C373.285 240.777 373.422 240.188 373.613 239.604L408.615 132.94C419.278 131.079 427.384 121.777 427.384 110.58C427.384 98.0443 417.222 87.8818 404.686 87.8818C392.15 87.8818 381.987 98.0443 381.987 110.58C381.987 112.582 382.246 114.523 382.733 116.372L286.069 167.202C280.714 170.018 274.098 167.607 271.809 162.006L232.163 64.9688ZM56.42 241.364H373.061V296.958C373.061 306.394 365.412 314.043 355.977 314.043H73.5044C64.069 314.043 56.42 306.394 56.42 296.958V241.364ZM254.807 185.737C254.807 190.999 253.771 196.209 251.757 201.07C249.744 205.932 246.793 210.349 243.073 214.069C239.352 217.79 234.935 220.741 230.074 222.755C225.213 224.768 220.003 225.805 214.742 225.804C209.119 225.804 202.49 225.523 197.998 221.708C192.869 217.356 192.15 210.441 193.554 204.205C194.19 201.082 195.573 198.16 197.585 195.688C199.597 193.217 202.178 191.27 205.107 190.014C207.729 188.977 210.528 188.457 213.348 188.482C218.962 188.482 222.431 190.133 225.288 191.493C227.287 192.445 228.986 193.254 230.91 193.254C234.052 193.254 236.791 191.426 236.791 187.674C236.791 181.482 226.138 175.896 213.959 175.896C194.222 175.896 181.662 188.613 179.717 205.209C176.743 199.861 175.034 193.903 174.72 187.792C174.406 181.681 175.496 175.58 177.905 169.955C180.315 164.33 183.981 159.333 188.622 155.345C193.262 151.357 198.755 148.484 204.678 146.948C210.601 145.412 216.797 145.253 222.791 146.483C228.785 147.713 234.417 150.299 239.257 154.044C244.096 157.788 248.014 162.591 250.709 168.085C253.405 173.578 254.806 179.616 254.807 185.735V185.737ZM207.557 215.946C210.976 215.946 213.748 213.174 213.748 209.754C213.748 206.335 210.976 203.563 207.557 203.563C204.137 203.563 201.365 206.335 201.365 209.754C201.365 213.174 204.137 215.946 207.557 215.946ZM231.725 209.755C231.725 213.174 228.953 215.946 225.533 215.946C222.114 215.946 219.342 213.174 219.342 209.755C219.342 206.335 222.114 203.563 225.533 203.563C228.953 203.563 231.725 206.335 231.725 209.755Z"
    />
  </svg>
);

// Filled gift box for free-plan / teaching-session badges. Mirrors the glyph
// used by FreePlanAccentIcon so the modal badge reads as the same brand mark
// as the home-page card chip.
export const MiniGiftIcon = (props: { style?: React.CSSProperties }) => (
  <svg
    viewBox="0 0 24 24"
    width={14}
    height={14}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    style={props.style}
  >
    <path d="M20 7h-3.2A3 3 0 0 0 12 3a3 3 0 0 0-4.8 4H4a1 1 0 0 0-1 1v3h18V8a1 1 0 0 0-1-1zm-8-2a1 1 0 1 1-1 1 1 1 0 0 1 1-1zm-3 0a1 1 0 1 1-1 1 1 1 0 0 1 1-1zM3 12v7a2 2 0 0 0 2 2h6v-9H3zm10 9h6a2 2 0 0 0 2-2v-7h-8v9z" />
  </svg>
);

export const ArrowIcon = (props: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={props.size ?? 16}
    height={props.size ?? 16}
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

export const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m5 12 5 5 9-11" />
  </svg>
);

export const ChatIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width={18}
    height={18}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

export const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width={16}
    height={16}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x={3} y={5} width={18} height={14} rx={2} />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

// ---------------- StickerHeader ----------------
type BadgeIcon = 'lock' | 'crown' | 'gift';

interface StickerHeaderProps {
  sticker: StickerName;
  badge: ReactNode;
  heading: ReactNode;
  tone?: ModalTone;
  /**
   * Icon shown before the badge text. Defaults to 'crown' because this modal
   * family is overwhelmingly school-plan-related; callers rendering a
   * teaching-session gate pass 'gift'.
   */
  badgeIcon?: BadgeIcon;
}

export type StickerName =
  | 'clap'
  | 'crown'
  | 'crown-filled'
  | 'gift-purple'
  | 'lightbulb'
  | 'party'
  | 'praise'
  | 'rocket'
  | 'stars';

const stickerPath = (name: StickerName) => `/images/blocked-paywall-stickers/${name}.svg`;

const HeaderContainer = styled.div<{ $tone: ModalTone }>`
  background: ${p => TONE_BG[p.$tone]};
  padding: 18px 24px 16px;
  position: relative;
  overflow: hidden;
`;

const StickerImg = styled.img`
  position: absolute;
  right: -10px;
  top: -8px;
  width: 100px;
  height: 100px;
  transform: rotate(12deg);
  opacity: 0.95;
  pointer-events: none;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #fff;
  color: ${theme.colors.black};
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 10px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  position: relative;
  z-index: 1;
`;

const Heading = styled.h2`
  font-size: 22px;
  font-weight: 900;
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.15;
  color: ${theme.colors.black};
  position: relative;
  z-index: 1;
  max-width: calc(100% - 90px);
  font-family: 'Nunito', sans-serif;

  @media (max-width: 480px) {
    font-size: 18px;
    max-width: calc(100% - 70px);
  }
`;

const BadgeIconByName: Record<
  BadgeIcon,
  FunctionComponent<{ style?: React.CSSProperties }>
> = {
  lock: LockIcon,
  crown: MiniCrownIcon,
  gift: MiniGiftIcon,
};

// Brand color paired with each badge icon. Crown stays orange to match
// SchoolPlanAccentIcon; gift switches to purple to match FreePlanAccentIcon.
const BadgeIconColor: Record<BadgeIcon, string> = {
  lock: theme.colors.orange,
  crown: theme.colors.orange,
  gift: theme.colors.purple,
};

export const StickerHeader: FunctionComponent<StickerHeaderProps> = ({
  sticker,
  badge,
  heading,
  tone = 'beige',
  badgeIcon = 'crown',
}) => {
  const Icon = BadgeIconByName[badgeIcon];
  return (
    <HeaderContainer $tone={tone}>
      <StickerImg src={stickerPath(sticker)} alt="" />
      <Badge>
        <Icon style={{ color: BadgeIconColor[badgeIcon] }} />
        {badge}
      </Badge>
      <Heading>{heading}</Heading>
    </HeaderContainer>
  );
};

// ---------------- Body container ----------------
export const ModalBody = styled.div`
  padding: 18px 24px 20px;

  @media (max-width: 480px) {
    padding: 16px 18px 18px;
  }
`;

export const BodyText = styled.p`
  font-size: 15px;
  line-height: 1.5;
  color: ${theme.colors.black};
  margin: 0 0 14px;
  font-weight: 700;
  font-family: 'Nunito', sans-serif;

  strong {
    font-weight: 900;
  }
`;

// ---------------- Shared layout helpers ----------------
// Used by multiple modals (SchoolPlanTrialModal, WeeklySessionLimitModal,
// PremiumFeatureGateModal). Keep here so the thanks/lesson-starting variants
// stay visually identical across modal families.
export const Centered = styled.div`
  text-align: center;
  margin-top: 12px;
`;

export const ThanksBody = styled.div`
  padding: 30px 26px 26px;
  text-align: center;
`;

export const ThanksSticker = styled.img`
  width: 80px;
  height: 80px;
  margin: 0 auto 12px;
  display: block;
`;

// ---------------- Primary button ----------------
interface OrangeBtnProps {
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  children: ReactNode;
  block?: boolean;
  dark?: boolean; // ink-coloured variant (used in "Chat with us now")
}

const PrimaryButton = styled.button<{ $block: boolean; $dark: boolean }>`
  width: ${p => (p.$block ? '100%' : 'auto')};
  background: ${p => (p.$dark ? theme.colors.black : theme.colors.orange)};
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 11px 18px;
  font-size: 14px;
  font-weight: 800;
  font-family: 'Nunito', sans-serif;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  transition: background 0.4s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover:not(:disabled) {
    background: ${p => (p.$dark ? '#000' : theme.colors.orangeDark)};
  }

  &:disabled {
    background: #efdfc8;
    color: rgba(40, 47, 51, 0.5);
    cursor: not-allowed;
    box-shadow: none;
  }
`;

export const OrangeBtn: FunctionComponent<OrangeBtnProps> = ({
  onClick,
  disabled,
  type = 'button',
  children,
  block = true,
  dark = false,
}) => (
  <PrimaryButton
    onClick={onClick}
    disabled={disabled}
    type={type}
    $block={block}
    $dark={dark}
  >
    {children}
  </PrimaryButton>
);

// ---------------- Ghost (text-link) button ----------------
const GhostBtn = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.black};
  font-size: 14px;
  font-weight: 700;
  font-family: 'Nunito', sans-serif;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: ${theme.colors.orangeDark};
  }
`;

interface LinkButtonProps {
  onClick?: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  href?: string;
  target?: string;
  children: ReactNode;
}

export const LinkButton: FunctionComponent<LinkButtonProps> = ({
  onClick,
  href,
  target,
  children,
}) => {
  if (href) {
    return (
      <GhostBtn
        as="a"
        href={href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        onClick={onClick}
      >
        {children}
      </GhostBtn>
    );
  }
  return <GhostBtn onClick={onClick}>{children}</GhostBtn>;
};

// ---------------- Email form ----------------
interface EmailFormProps {
  onSubmit: (email: string) => void;
  ctaLabel?: ReactNode;
  autoFocus?: boolean;
}

const EmailWrap = styled.form<{ $focused: boolean }>`
  background: #fff;
  border: 2px solid ${p => (p.$focused ? theme.colors.orange : 'transparent')};
  border-radius: 12px;
  padding: 5px;
  display: flex;
  gap: 5px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
`;

const EmailIconWrap = styled.div`
  display: flex;
  align-items: center;
  padding-left: 10px;
  color: rgba(40, 47, 51, 0.5);
`;

const EmailInput = styled.input`
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  padding: 10px 4px;
  font-size: 13px;
  font-family: 'Nunito', sans-serif;
  font-weight: 700;
  color: ${theme.colors.black};
  outline: none;

  &::placeholder {
    color: rgba(40, 47, 51, 0.4);
  }
`;

const SubmitBtn = styled.button`
  background: ${theme.colors.orange};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 800;
  font-family: 'Nunito', sans-serif;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  transition: background 0.4s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover:not(:disabled) {
    background: ${theme.colors.orangeDark};
  }

  &:disabled {
    background: #efdfc8;
    color: rgba(40, 47, 51, 0.5);
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EmailForm: FunctionComponent<EmailFormProps> = ({
  onSubmit,
  ctaLabel,
  autoFocus,
}) => {
  const [email, setEmail] = useState('');
  const [focused, setFocused] = useState(false);
  const valid = EMAIL_RE.test(email);

  return (
    <EmailWrap
      $focused={focused || valid}
      onSubmit={e => {
        e.preventDefault();
        if (valid) onSubmit(email);
      }}
    >
      <EmailIconWrap>
        <MailIcon />
      </EmailIconWrap>
      <EmailInput
        type="email"
        value={email}
        autoFocus={autoFocus}
        onChange={e => setEmail(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="your.email@school.edu"
      />
      <SubmitBtn type="submit" disabled={!valid}>
        {ctaLabel ?? (
          <T
            keyName="BlockedPaywall.EmailCta.Default"
            defaultValue="Help me get unlimited access"
          />
        )}
      </SubmitBtn>
    </EmailWrap>
  );
};

// ---------------- Tip callout ----------------
interface TipProps {
  children: ReactNode;
}

const TipWrap = styled.div`
  margin-top: 16px;
  background: ${LANDING_BEIGE};
  border: 1px solid ${theme.colors.beigeDark};
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const TipIcon = styled.img`
  width: 28px;
  height: 28px;
  flex: none;
  margin-top: -4px;
`;

const TipBody = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${theme.colors.black};
  font-weight: 700;
  flex: 1;
  font-family: 'Nunito', sans-serif;

  strong {
    font-weight: 900;
  }
`;

export const Tip: FunctionComponent<TipProps> = ({ children }) => (
  <TipWrap>
    <TipIcon src={stickerPath('lightbulb')} alt="" />
    <TipBody>{children}</TipBody>
  </TipWrap>
);

// ---------------- Inline thanks panel (after sidebar email submit) ----------------
const InlineThanksWrap = styled.div`
  margin-top: 4px;
  background: ${LANDING_BEIGE};
  border: 1px solid ${theme.colors.beigeDark};
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  gap: 10px;
  align-items: center;
`;

const InlineThanksIcon = styled.div`
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background: ${theme.colors.orange};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InlineThanksBody = styled.div`
  font-size: 13px;
  line-height: 1.45;
  color: ${theme.colors.black};
  font-weight: 700;
  flex: 1;
  font-family: 'Nunito', sans-serif;

  strong {
    font-weight: 900;
  }
`;

export const InlineThanksPanel: FunctionComponent<{ email: string }> = ({ email }) => (
  <InlineThanksWrap>
    <InlineThanksIcon>
      <CheckIcon />
    </InlineThanksIcon>
    <InlineThanksBody>
      <T
        keyName="BlockedPaywall.InlineThanks.Body"
        defaultValue="<bold>Thanks!</bold> We'll reach out to <emailStyle>{email}</emailStyle> shortly."
        params={{
          email,
          bold: <strong />,
          emailStyle: <strong style={{ color: '#E58332' }} />,
        }}
      />
    </InlineThanksBody>
  </InlineThanksWrap>
);

// ---------------- Step card (numbered, used in State 4) ----------------
interface StepCardProps {
  n: number;
  children: ReactNode;
}

const StepWrap = styled.div`
  background: #fff;
  border: 1px solid ${theme.colors.beigeDark};
  border-radius: 10px;
  padding: 14px 14px 12px;
  position: relative;
  box-shadow: 0 2px 20px 0 #deaf8d33;
`;

const StepBadge = styled.div`
  position: absolute;
  top: -9px;
  left: 14px;
  background: ${theme.colors.orange};
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.08em;
  padding: 3px 10px;
  border-radius: 999px;
  text-transform: uppercase;
`;

const StepBody = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${theme.colors.black};
  line-height: 1.45;
  margin-top: 4px;
  font-family: 'Nunito', sans-serif;
`;

export const StepCard: FunctionComponent<StepCardProps> = ({ n, children }) => (
  <StepWrap>
    <StepBadge>
      <T keyName="BlockedPaywall.Step" defaultValue="Step {n}" params={{ n }} />
    </StepBadge>
    <StepBody>{children}</StepBody>
  </StepWrap>
);

// ---------------- "Or" divider used between primary CTA + email form ----------------
export const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0;
  color: rgba(40, 47, 51, 0.5);
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-family: 'Nunito', sans-serif;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-top: 1px solid ${theme.colors.beigeDark};
  }
`;

// ---------------- Footer link row ----------------
export const FooterLinkRow = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  font-size: 12px;
`;

export const FooterDotDivider = styled.span`
  color: ${theme.colors.grey};
`;

// ---------------- Grid for step cards (2-up) ----------------
export const StepCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// ---------------- "Did you know?" heading (State 4 / Home) ----------------
export const SectionHeading = styled.h3`
  font-size: 16px;
  font-weight: 900;
  margin: 6px 0 10px;
  font-family: 'Nunito', sans-serif;
  letter-spacing: -0.01em;
  color: ${theme.colors.black};
`;
